import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Briefcase, Scale, Shield, Building2, FileText, Lock, Award } from 'lucide-react';
import { api } from '../../api/client';
import { showToast } from '../../components/common/Toast';
import Modal from '../../components/common/Modal';

export default function AdminServicesPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    titleUz: '',
    titleRu: '',
    titleEn: '',
    shortDescUz: '',
    shortDescRu: '',
    shortDescEn: '',
    fullDescUz: '',
    fullDescRu: '',
    fullDescEn: '',
    icon: 'Briefcase',
    isActive: true,
  });

  const loadServices = () => {
    setLoading(true);
    api.get('/services?includeInactive=true')
      .then((res) => {
        if (res?.success) setServices(res.services || []);
      })
      .catch((err) => showToast(err.message, 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadServices();
  }, []);

  const handleOpenAdd = () => {
    setEditingService(null);
    setFormData({
      titleUz: '',
      titleRu: '',
      titleEn: '',
      shortDescUz: '',
      shortDescRu: '',
      shortDescEn: '',
      fullDescUz: '',
      fullDescRu: '',
      fullDescEn: '',
      icon: 'Briefcase',
      isActive: true,
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (srv) => {
    setEditingService(srv);
    setFormData({
      titleUz: srv.titleUz,
      titleRu: srv.titleRu || '',
      titleEn: srv.titleEn || '',
      shortDescUz: srv.shortDescUz,
      shortDescRu: srv.shortDescRu || '',
      shortDescEn: srv.shortDescEn || '',
      fullDescUz: srv.fullDescUz || '',
      fullDescRu: srv.fullDescRu || '',
      fullDescEn: srv.fullDescEn || '',
      icon: srv.icon || 'Briefcase',
      isActive: srv.isActive,
    });
    setIsModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.titleUz || !formData.shortDescUz) {
      showToast('O‘zbekcha nom va qisqa tavsif majburiy', 'error');
      return;
    }

    setSaving(true);
    try {
      if (editingService) {
        const res = await api.put(`/services/${editingService.id}`, formData);
        if (res?.success) {
          showToast('Xizmat yangilandi', 'success');
          setIsModalOpen(false);
          loadServices();
        }
      } else {
        const res = await api.post('/services', formData);
        if (res?.success) {
          showToast('Yangi xizmat qo‘shildi', 'success');
          setIsModalOpen(false);
          loadServices();
        }
      }
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Haqiqatan ham "${title}" xizmatini o‘chirmoqchimisiz?`)) return;
    try {
      const res = await api.delete(`/services/${id}`);
      if (res?.success) {
        showToast('Xizmat o‘chirildi', 'success');
        loadServices();
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-lg font-bold text-slate-900">
            Amaliyot Yo‘nalishlari Katalogi ({services.length})
          </h2>
          <p className="text-xs text-slate-500">
            Saytdagi barcha yuridik xizmatlar va tavsiflar boshqaruvi
          </p>
        </div>

        <button
          onClick={handleOpenAdd}
          className="inline-flex items-center px-4 py-2 text-xs font-semibold text-[#091322] bg-[#c5a880] hover:bg-[#d4b993] rounded shadow-sm transition-colors"
        >
          <Plus className="w-4 h-4 mr-1.5" />
          Yangi amaliyot qo‘shish
        </button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 shadow-2xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="py-3 px-4">Xizmat Nomi (O‘zbekcha)</th>
                <th className="py-3 px-4">Ruscha Nomi</th>
                <th className="py-3 px-4">Inglizcha Nomi</th>
                <th className="py-3 px-4">Advokatlar</th>
                <th className="py-3 px-4">Holat</th>
                <th className="py-3 px-4 text-right">Amallar</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-slate-400">Yuklanmoqda...</td>
                </tr>
              ) : (
                services.map((s) => (
                  <tr key={s.id} className="hover:bg-slate-50 transition-colors">
                    <td className="py-3 px-4 font-semibold text-slate-900">
                      {s.titleUz}
                    </td>
                    <td className="py-3 px-4 text-slate-600">
                      {s.titleRu || '—'}
                    </td>
                    <td className="py-3 px-4 text-slate-600">
                      {s.titleEn || '—'}
                    </td>
                    <td className="py-3 px-4 font-mono text-slate-700">
                      {s.lawyers?.length || 0} ta
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border ${s.isActive ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                        {s.isActive ? 'Faol' : 'Nofaol'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="inline-flex space-x-2">
                        <button
                          onClick={() => handleOpenEdit(s)}
                          className="p-1 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded"
                          title="Tahrirlash"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(s.id, s.titleUz)}
                          className="p-1 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded"
                          title="O‘chirish"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingService ? `Xizmatni Tahrirlash: ${editingService.titleUz}` : 'Yangi Amaliyot Qo‘shish'}
        maxWidth="max-w-3xl"
      >
        <form onSubmit={handleSave} className="space-y-4 text-xs">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block font-semibold text-slate-700 uppercase mb-1">Nomi (UZ) *</label>
              <input
                type="text"
                value={formData.titleUz}
                onChange={(e) => setFormData({ ...formData, titleUz: e.target.value })}
                className="w-full px-3 py-2 border rounded focus:border-[#c5a880]"
                required
              />
            </div>
            <div>
              <label className="block font-semibold text-slate-700 uppercase mb-1">Nomi (RU)</label>
              <input
                type="text"
                value={formData.titleRu}
                onChange={(e) => setFormData({ ...formData, titleRu: e.target.value })}
                className="w-full px-3 py-2 border rounded focus:border-[#c5a880]"
              />
            </div>
            <div>
              <label className="block font-semibold text-slate-700 uppercase mb-1">Nomi (EN)</label>
              <input
                type="text"
                value={formData.titleEn}
                onChange={(e) => setFormData({ ...formData, titleEn: e.target.value })}
                className="w-full px-3 py-2 border rounded focus:border-[#c5a880]"
              />
            </div>

            <div className="sm:col-span-3">
              <label className="block font-semibold text-slate-700 uppercase mb-1">Qisqa Tavsif (UZ) *</label>
              <textarea
                rows={2}
                value={formData.shortDescUz}
                onChange={(e) => setFormData({ ...formData, shortDescUz: e.target.value })}
                className="w-full px-3 py-2 border rounded focus:border-[#c5a880]"
                required
              />
            </div>

            <div className="sm:col-span-3">
              <label className="block font-semibold text-slate-700 uppercase mb-1">Batafsil Tavsif (UZ)</label>
              <textarea
                rows={4}
                value={formData.fullDescUz}
                onChange={(e) => setFormData({ ...formData, fullDescUz: e.target.value })}
                className="w-full px-3 py-2 border rounded focus:border-[#c5a880]"
              />
            </div>

            <div className="flex items-center space-x-2 pt-2 sm:col-span-3">
              <input
                type="checkbox"
                id="srv-active"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="rounded border-slate-300 text-[#091322]"
              />
              <label htmlFor="srv-active" className="font-semibold text-slate-800">
                Xizmat turi saytda faol ko‘rinsin
              </label>
            </div>
          </div>

          <div className="pt-4 border-t flex justify-end space-x-2">
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="px-4 py-2 border rounded text-slate-700 hover:bg-slate-50"
            >
              Bekor qilish
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-[#091322] text-[#c5a880] font-semibold rounded hover:bg-[#11223b]"
            >
              {saving ? 'Saqlanmoqda...' : 'Saqlash'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
